import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plug, Trash2, Shield, ShieldCheck, Eye, Users as UsersIcon, UserPlus, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const ROLES = [
    { v: "admin", label: "Admin", desc: "Full access · user mgmt" },
    { v: "broker", label: "Broker", desc: "Create & manage deals" },
    { v: "analyst", label: "Analyst", desc: "Read-only access" },
    { v: "external_viewer", label: "External viewer", desc: "Opportunities only" },
];

const ROLE_ICON = { admin: ShieldCheck, broker: Shield, analyst: Eye, external_viewer: UsersIcon };

function IntegrationsPanel() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/integrations").then((r) => setItems(r.data)).catch(() => {}); }, []);
    if (!items.length) return null;
    return (
        <div className="border border-border rounded-md bg-white overflow-hidden mb-6" data-testid="integrations-panel">
            <div className="px-5 py-4 border-b border-border">
                <h2 className="font-display font-bold tracking-tight flex items-center gap-2"><Plug className="w-4 h-4 text-gold" /> Integrations</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Planned connections — not available yet.</p>
            </div>
            <ul>
                {items.map((i) => (
                    <li key={i.key} className="px-5 py-3 border-b border-border/60 last:border-0 flex items-start justify-between gap-4" data-testid={`integration-${i.key}`}>
                        <div><div className="text-sm font-medium">{i.name}</div><div className="text-xs text-muted-foreground mt-0.5">{i.description}</div></div>
                        <Badge variant="outline" className="shrink-0">Coming soon</Badge>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function Settings() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (err) {
            if (err.response?.status === 403) setDenied(true);
            else toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    const changeRole = async (id, role) => {
        try {
            await api.patch(`/users/${id}/role`, { role });
            toast.success("Role updated");
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };
    const del = async (id) => {
        if (!confirm("Delete this user permanently?")) return;
        try {
            await api.delete(`/users/${id}`);
            toast.success("User deleted");
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    if (denied) {
        return (
            <div data-testid="settings-denied">
                <PageHeader title="Settings" subtitle="Admin-only area." />
                <div className="p-8">
                    <div className="border border-border rounded-md bg-muted/30 p-12 text-center">
                        <Shield className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
                        <h3 className="font-display font-bold text-lg tracking-tight mb-1">Admin access required</h3>
                        <p className="text-sm text-muted-foreground">Only administrators can manage users and roles.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="settings-page">
            <PageHeader title="Settings" subtitle="Manage team access and permissions." />

            <div className="p-6 lg:p-8 max-w-5xl space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {ROLES.map((r) => {
                        const Icon = ROLE_ICON[r.v];
                        const count = users.filter((u) => u.role === r.v).length;
                        return (
                            <div key={r.v} className="border border-border rounded-[6px] bg-white py-3 px-4">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Icon className="w-3.5 h-3.5 text-gold" strokeWidth={1.75} />
                                    <span className="text-[11px] uppercase tracking-wider text-[#64748B]">{r.label}</span>
                                </div>
                                <div className="font-mono text-[22px] font-semibold text-navy leading-tight">{count}</div>
                                <div className="text-[12px] text-[#94A3B8] mt-1">{r.desc}</div>
                            </div>
                        );
                    })}
                </div>

                <IntegrationsPanel />

                <div className="border border-border rounded-md bg-white overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                        <div>
                            <h2 className="font-display font-bold tracking-tight">Team members</h2>
                            <p className="text-xs text-muted-foreground mt-0.5">{users.length} total</p>
                        </div>
                        <InviteDialog onInvited={refresh} />
                    </div>

                    {loading ? (
                        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                    <th className="text-left px-5 py-3">Name</th>
                                    <th className="text-left px-5 py-3">Email</th>
                                    <th className="text-left px-5 py-3">Role</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u, i) => (
                                    <motion.tr
                                        key={u.id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: Math.min(i * 0.02, 0.2), duration: 0.25 }}
                                        className="border-b border-border/60 hover:bg-muted/30"
                                        data-testid={`user-row-${u.id}`}
                                    >
                                        <td className="px-5 py-3">
                                            <div className="font-medium">{u.name}</div>
                                            {u.id === user?.id && <Badge variant="outline" className="mt-1 text-[9px]">You</Badge>}
                                        </td>
                                        <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
                                        <td className="px-5 py-3">
                                            <Select
                                                value={u.role}
                                                onValueChange={(v) => changeRole(u.id, v)}
                                                disabled={u.id === user?.id}
                                            >
                                                <SelectTrigger className="w-44 h-8 text-xs" data-testid={`role-select-${u.id}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                disabled={u.id === user?.id}
                                                onClick={() => del(u.id)}
                                                data-testid={`delete-user-${u.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                            </Button>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function InviteDialog({ onInvited }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("broker");
    const [loading, setLoading] = useState(false);

    const INVITABLE_ROLES = [
        { v: "broker", label: "Broker" },
        { v: "analyst", label: "Analyst" },
        { v: "external_viewer", label: "External Viewer" },
    ];

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Email is required");
            return;
        }
        setLoading(true);
        try {
            await api.post("/users/invite", {
                email: email.trim().toLowerCase(),
                name: name.trim() || null,
                role,
            });
            toast.success(`Invite sent to ${email}`);
            setOpen(false);
            setEmail("");
            setName("");
            setRole("broker");
            onInvited();
        } catch (err) {
            if (err.response?.status === 503) {
                toast.error("Email not configured — contact support");
            } else {
                toast.error(err.response?.data?.detail || "Failed to send invite");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="gap-1.5 bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="invite-team-btn">
                    <UserPlus className="w-4 h-4" /> Invite
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="invite-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gold" /> Invite team member
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email *</Label>
                        <Input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="colleague@firm.com"
                            data-testid="invite-email-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name (optional)</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="John Smith"
                            data-testid="invite-name-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger data-testid="invite-role-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {INVITABLE_ROLES.map((r) => (
                                    <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="bg-foreground text-background hover:bg-neutral-800 hover:text-white"
                            data-testid="send-invite-btn"
                        >
                            {loading ? "Sending…" : "Send invite"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
